import {
  SpinWheelsService,
  type PostSpinWheelAdminRequest,
  type PutSpinWheelAdminRequest,
  type PutSpinWheelRegionsAdminRequest
} from '@/backend/market'
import { SpinWheelRepository } from '../../domain/repositories/SpinWheelRepository'
import { SpinWheelModel } from '../models/SpinWheelModel'
import type { SpinWheelFilterEntity } from '../../domain/entities/SpinWheelFilterEntity'
import { SpinWheelFilterModel } from '../models/SpinWheelFilterModel'
import { SpinWheelMiniModel } from '../models/SpinWheelMiniModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import type { SpinWheelMiniEntity } from '../../domain/entities/SpinWheelMiniEntity'
import type { SpinWheelEntity } from '../../domain/entities/SpinWheelEntity'
import type { CreateUpdateSpinWheelEntity } from '../../domain/entities/CreateUpdateSpinWheelEntity'
import { CreateUpdateSpinWheelModel } from '../models/CreateUpdateSpinWheelModel'
import type { UserMiniEntity } from '@/features/user/domain/entities/UserMiniEntity'
import { UserMiniModel } from '@/features/user/data/models/UserMiniModel'
import type { UpdateSpinWheelUserEntity } from '../../domain/entities/UpdateSpinWheelUserEntity'
import { UpdateSpinWheelUserModel } from '../models/UpdateSpinWheelUserModel'
import type { PrizeMiniEntity } from '@/features/prize/domain/entities/PrizeMiniEntity'
import { PrizeMiniModel } from '@/features/prize/data/models/PrizeMiniModel'
import type { UpdateSpinWheelPrizesEntity } from '../../domain/entities/UpdateSpinWheelPrizesEntity'
import { UpdateSpinWheelPrizesModel } from '../models/UpdateSpinWheelPrizesModel'
import type { UpdateSpinWheelBranchEntity } from '../../domain/entities/UpdateSpinWheelBranchEntity'
import { UpdateSpinWheelBranchModel } from '../models/UpdateSpinWheelBranchModel'
import type { UpdateSpinWheelRegionEntity } from '../../domain/entities/UpdateSpinWheelRegionEntity'
import { UpdateSpinWheelRegionModel } from '../models/UpdateSpinWheelRegionModel'
import type { BranchMiniEntity } from '@/features/branch/domain/entities/BranchMiniEntity'
import { BranchMiniModel } from '@/features/branch/data/models/BranchMiniModel'
import type { RegionMiniEntity } from '@/features/region/domain/entities/RegionMiniEntity'
import { SpinWheelRegionModel } from '../models/SpinWheelRegionModel'
import type { SpinWheelRegionEntity } from '../../domain/entities/SpinWheelRegionEntity'
import { RegionMiniModel } from '@/features/region/data/models/RegionMiniModel'

export class SpinWheelRepositoryImpl implements SpinWheelRepository {
  async getSpinWheels(
    filterData: SpinWheelFilterEntity
  ): Promise<PaginateEntity<SpinWheelMiniEntity[]> | { error: true }> {
    const spinWheelFilterModel = new SpinWheelFilterModel(filterData)
    try {
      const response = await SpinWheelsService.getPrizesAdmin(spinWheelFilterModel)
      const spinModels = response.items?.map((item) => new SpinWheelMiniModel(item)) ?? []
      const spinWheelResponse = new PaginateEntity<SpinWheelMiniModel[]>(response, spinModels)

      return spinWheelResponse
    } catch {
      return { error: true }
    }
  }
  async getSpinWheel(id: number): Promise<SpinWheelEntity | { error: true }> {
    try {
      const response = await SpinWheelsService.getSpinWheelByIdAdmin({
        id
      })
      const spinModel = new SpinWheelModel(response)
      return spinModel
    } catch {
      return { error: true }
    }
  }
  async createSpinWheel(
    spinData: CreateUpdateSpinWheelEntity
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateSpinWheelModel = new CreateUpdateSpinWheelModel(spinData)
    try {
      await SpinWheelsService.createSpinWheelAdmin({
        requestBody: createUpdateSpinWheelModel as PostSpinWheelAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateSpinWheel(
    spinData: CreateUpdateSpinWheelEntity,
    id: number
  ): Promise<{ success: true } | { error: true }> {
    const createUpdateSpinWheelModel = new CreateUpdateSpinWheelModel(spinData)
    try {
      await SpinWheelsService.updateSpinWheelAdmin({
        id,
        requestBody: createUpdateSpinWheelModel as PutSpinWheelAdminRequest
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async deleteSpinWheel(id: number): Promise<{ success: true } | { error: true }> {
    try {
      await SpinWheelsService.deleteSpinWheelAdmin({
        id
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async getSpinWheelUsers(id: number): Promise<UserMiniEntity[] | { error: true }> {
    try {
      const response = await SpinWheelsService.getSpinWheelUsersAdmin({
        id
      })
      const spinModel =
        response.users?.map((item) => new UserMiniModel(item as UserMiniEntity)) ?? []
      return spinModel
    } catch {
      return { error: true }
    }
  }
  async updateSpinWheelUsers(
    id: number,
    users: UpdateSpinWheelUserEntity
  ): Promise<UserMiniEntity[] | { error: true }> {
    const updateSpinWheelUserModel = new UpdateSpinWheelUserModel(users)
    try {
      const response = await SpinWheelsService.updateSpinWheelUsersAdmin({
        id,
        requestBody: updateSpinWheelUserModel
      })
      const spinModel =
        response.users?.map((item) => new UserMiniModel(item as UserMiniEntity)) ?? []
      return spinModel
    } catch {
      return { error: true }
    }
  }
  async getSpinWheelPrizes(id: number): Promise<PrizeMiniEntity[] | { error: true }> {
    try {
      const response = await SpinWheelsService.getSpinWheelPrizesAdmin({
        id
      })
      const spinModel = response.prizes?.map((item) => new PrizeMiniModel(item)) ?? []
      return spinModel
    } catch {
      return { error: true }
    }
  }
  async updateSpinWheelPrizes(
    id: number,
    prizes: UpdateSpinWheelPrizesEntity
  ): Promise<PrizeMiniEntity[] | { error: true }> {
    prizes.prizes?.forEach((prize, index) => {
      prize.order = index + 1
    })
    const updateSpinWheelPrizesModel = new UpdateSpinWheelPrizesModel(prizes)
    try {
      const response = await SpinWheelsService.updateSpinWheelPrizesAdmin({
        id,
        requestBody: updateSpinWheelPrizesModel as UpdateSpinWheelPrizesEntity
      })
      const spinModel = response.prizes?.map((item) => new PrizeMiniModel(item)) ?? []
      return spinModel
    } catch {
      return { error: true }
    }
  }

  async getSpinWheelBranches(id: number): Promise<BranchMiniEntity[] | { error: true }> {
    try {
      const response = await SpinWheelsService.getSpinWheelBranchesAdmin({
        id
      })
      const branchModels = response.branches?.map((item) => new BranchMiniModel(item)) ?? []
      return branchModels
    } catch {
      return { error: true }
    }
  }

  async updateSpinWheelBranches(
    id: number,
    branches: UpdateSpinWheelBranchEntity
  ): Promise<BranchMiniEntity[] | { error: true }> {
    const updateSpinWheelBranchModel = new UpdateSpinWheelBranchModel(branches)
    try {
      const response = await SpinWheelsService.putSpinWheelBranchesAdmin({
        id,
        requestBody: updateSpinWheelBranchModel
      })
      const branchModels = response.branches?.map((item) => new BranchMiniModel(item)) ?? []
      return branchModels
    } catch {
      return { error: true }
    }
  }

  async getSpinWheelRegions(id: number): Promise<SpinWheelRegionEntity[] | { error: true }> {
    try {
      const response = await SpinWheelsService.getSpinWheelRegionsAdmin({
        id
      })
      const regionModels = response.regions?.map((item) => new SpinWheelRegionModel(item)) ?? []
      return regionModels
    } catch {
      return { error: true }
    }
  }

  async updateSpinWheelRegions(
    id: number,
    regions: UpdateSpinWheelRegionEntity
  ): Promise<RegionMiniEntity[] | { error: true }> {
    const updateSpinWheelRegionModel = new UpdateSpinWheelRegionModel(regions)
    try {
      const response = await SpinWheelsService.putSpinWheelRegionsAdmin({
        id,
        requestBody: updateSpinWheelRegionModel as PutSpinWheelRegionsAdminRequest
      })
      const regionModels = response.regions?.map((item) => new RegionMiniModel(item)) ?? []
      return regionModels
    } catch {
      return { error: true }
    }
  }
}
